<script lang="ts" setup>
import type { PickUpPointType } from '~/api/common';

const MAP_DATA_VERSION = 1;
const MAP_DATA_STORAGE_KEY = 'map-data';
const TEST_ID = 'order-map-modal';

const props = defineProps<{
  visible: boolean;
}>();
const visible = toRef(props, 'visible');
const emit = defineEmits<{
  (event: 'update:visible', value: boolean): void;
  (event: 'select', value: PickUpPointType): void;
}>();

const { t } = useI18n();
const commonStore = useCommonStore();

const [modalVisible, onToggleVisible] = useExternalModalState(visible, (v) =>
  emit('update:visible', v),
);

let map: typeof ymaps = null;

const initMarkers = () => {
  if (!map || !commonStore.pickUpPoints.length) return;

  const objectManager = new ymaps.ObjectManager({
    clusterize: true,
    gridSize: 80,
    clusterDisableClickZoom: true,
  });
  objectManager.objects.options.set('preset', 'islands#dotIcon');
  objectManager.objects.options.set('iconColor', '#121212');
  objectManager.clusters.options.set('preset', 'islands#clusterIcons');
  objectManager.clusters.options.set('clusterIconColor', '#121212');
  map.geoObjects.add(objectManager);
  objectManager.add({
    type: 'FeatureCollection',
    features: commonStore.pickUpPoints.map((item) => ({
      type: 'Feature',
      id: item.id,
      geometry: {
        type: 'Point',
        coordinates: item.coords,
      },
      properties: {
        balloonContentHeader: `<span class="orderMapModal__itemTitle">${t('balloon.header')}</span>`,
        balloonContentBody: `<p class="orderMapModal__itemDescription">${t('balloon.description', item)}</p>`,
        balloonContentFooter: `<button class="baseButton baseButton_dark baseButton_xs" data-action="select" data-point-id="${item.id}">${t('balloon.action')}</button>`,
        clusterCaption: `<span class="orderMapModal__itemLabel">${item.service}</span>`,
        hintContent: `<span class="orderMapModal__itemLabel">${item.service}</span>`,
      },
    })),
  });
};

const init = async () => {
  while (typeof ymaps === 'undefined' && modalVisible.value) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  ymaps.ready(() => {
    let center = [55.750969, 37.617102];
    let zoom = 14;
    if (
      typeof localStorage !== 'undefined' &&
      localStorage[MAP_DATA_STORAGE_KEY]
    ) {
      const cachedData = JSON.parse(localStorage[MAP_DATA_STORAGE_KEY]);
      if (cachedData.version === MAP_DATA_VERSION) {
        center = cachedData.center;
        zoom = cachedData.zoom;
      }
    }
    map = new ymaps.Map(
      'yandex-map',
      { center, zoom },
      { searchControlProvider: 'yandex#search' },
    );
    map.events.add('boundschange', (event: MapsEvent) => {
      const center = event.get('newCenter');
      const zoom = event.get('newZoom');
      localStorage[MAP_DATA_STORAGE_KEY] = JSON.stringify({
        version: MAP_DATA_VERSION,
        center,
        zoom,
      });
    });

    initMarkers();
  });
};

const onMapClick = (e: Event) => {
  const target = e.target as HTMLElement;
  if (target.dataset.action === 'select') {
    const point = commonStore.pickUpPoints.find(
      (el) => el.id.toString() === target.dataset.pointId,
    );
    if (point) emit('select', point);
  }
};

watch(modalVisible, () => {
  if (modalVisible.value) init();
  else {
    map?.destroy();
    map = null;
  }
});

onMounted(() => {
  commonStore.fetchPickUpPoints().then(() => {
    initMarkers();
  });
});

onBeforeUnmount(() => {
  map?.destroy();
  onToggleVisible(false);
});
</script>

<template>
  <BaseModal
    :visible="modalVisible"
    @update:visible="onToggleVisible"
    fullscreen
    :aria-title="t('aria_title')"
    :aria-describedby="t('aria_description')"
    container-class="orderMapModal__container"
    :data-test-id="TEST_ID"
  >
    <div id="yandex-map" class="orderMapModal__map" @click="onMapClick" />
  </BaseModal>
</template>

<i18n>
ru:
  aria_title: Карта пунктов выдачи заказа
  aria_description: Просмотр пунктов выдачи заказов на карте
  balloon:
    header: Пункт выдачи заказа
    description: |
      Адрес: {address}
      Сервис выдачи заказа: {service}
    action: Выбрать

en:
  aria_title: Order pickup points map
  aria_description: View of order pickup points on the map
  balloon:
    header: Order pickup point
    description: |
      Address: {address}
      Order pickup service: {service}
    action: Select
</i18n>

<style lang="scss">
.orderMapModal {
  &__container {
    display: flex;
    padding: 68px 0;
  }

  &__map {
    width: 100%;
    height: 100%;
  }

  &__itemTitle {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.2px;
    color: #121212;
    margin-bottom: 12px;

    @include mq('sm') {
      font-size: 20px;
      line-height: 28px;
    }
  }

  &__itemDescription {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #121212;
    margin-top: 0;
    margin-bottom: 14px;
    white-space: pre-wrap;
  }

  &__itemLabel {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #121212;
  }
}
</style>
